import { View } from 'foundations-components/components/View';
import { useMediaBySize } from 'pricing-pages-ui-components/hooks/useMedia';
import { HsocBodyBackgroundStyles } from 'pricing-pages-ui-components/stylesheets/HsocBodyBackground.styles';
import HsocFeatureTableLoadingCard from 'pricing-pages-ui-hsoc/components/feature-table/HsocFeatureTableLoadingCard';
import HsocMerchCardGrid from 'pricing-pages-ui-hsoc/components/HsocMerchCardGrid';
import HsocMerchCardGridSkeleton from 'pricing-pages-ui-hsoc/components/HsocMerchCardGridSkeleton';
import { HsocV2PageHero } from 'pricing-pages-ui-hsoc/components/HsocV2PageHero';
import HelpMeChooseInline from 'pricing-pages-ui-hsoc/components/plan-quiz/HelpMeChooseInline';
import { PlanQuizProvider } from 'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizContext';
import { PlanQuizDataProvider } from 'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizDataContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { HUBSPOT_ON_CREDITS_PAGE } from '../../components/error-boundary/ErrorBoundaryNames';
import HsocCreditsExplainerSection from '../../components/hsoc-credits-explainer/HsocCreditsExplainerSection';
import HsocFaqsSection from '../../components/hsoc-faqs/HsocFaqsSection';
import PublicCurrencyPicker from '../../components/PublicCurrencyPicker';
import { PublicMerchCardsError } from '../../components/merch-card/PublicMerchCardsError';
import { PublicPageWrapper } from '../../components/PublicPageWrapper';
import { useCurrency } from '../../context/CurrencyContext';
import { usePublicFeatureFlags } from '../../hooks/usePublicFeatureFlags';
import { usePublicPageUrlState } from '../../hooks/usePublicPageUrlState';
import { FeatureFlags } from '../../constants/FeatureFlags';
import { Screens } from '../../types/PublicNavItem';
import { createDeferredLoadable } from '../../utils/createDeferredLoadable';
import { TRACKING_SOURCES, trackClickedLink, trackHsocPlanQuizEvent, trackPageView, trackSectionViewed, trackTermSelectorInteraction } from '../../utils/tracking';
import { isHsocTierName } from './hsocPublicTiers';
import { HsocV2PublicPurchaseMotionContainer } from './HsocV2PublicPurchaseMotionContainer';
import { PublicFscCustomizePlanLink } from './PublicFscCustomizePlanLink';
import { useHsocPublicPricing } from './useHsocPublicPricing';
import { useHsocPublicTierOptions } from './useHsocPublicTierOptions';
import { usePPFE0016ExperimentEffect } from '../../utils/experiments/ppfe0016';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LoadablePlanQuizModal = createDeferredLoadable(() => import( /* webpackChunkName: "plan-quiz-modal" */'pricing-pages-ui-hsoc/components/plan-quiz/PlanQuizModal').then(m => ({
  default: m.default
})));

/* Reserve the feature table's space with the skeleton while its code-split
 * chunk loads, so it comes up with the merch cards instead of popping in after
 * the chunk resolves. Only this lightweight skeleton card ships in the initial
 * bundle; the heavy table stays deferred. */
function PublicHsocFeatureTableLoading() {
  return /*#__PURE__*/_jsx(View, {
    paddingInline: "800",
    marginBlockEnd: "1400",
    children: /*#__PURE__*/_jsx(HsocFeatureTableLoadingCard, {})
  });
}

/* The feature table is kept out of the initial public bundle: it is a large,
 * below-the-fold section. */
const LoadablePublicHsocFeatureTable = createDeferredLoadable(() => import( /* webpackChunkName: "public-hsoc-feature-table" */'./feature-table/PublicHsocFeatureTable').then(m => ({
  default: m.default
})), PublicHsocFeatureTableLoading);
function HsocPublicPageContent({
  isPreview
}) {
  const isMd = useMediaBySize('md');
  const {
    displayedCurrencyCode,
    isLoading: isCurrencyLoading
  } = useCurrency();
  const {
    selectedTerm,
    setSelectedTerm
  } = usePublicPageUrlState();

  /* The tier-options query is owned here (fetched once, shared with the cards
   * and the feature table), so its status is handled in one place and both
   * surfaces read the same tier data + billing term. Skipped while currency is
   * still resolving so it doesn't fire with CurrencyContext's serverDefault
   * fallback (see useHsocPublicTierOptions for why that misses the early
   * requester's cache). */
  const tierOptions = useHsocPublicTierOptions(displayedCurrencyCode, {
    skip: isCurrencyLoading
  });
  const {
    cards,
    showTermToggle
  } = useHsocPublicPricing(tierOptions, displayedCurrencyCode, selectedTerm);
  const showCards = tierOptions.status === 'ready' && cards.length > 0;
  usePPFE0016ExperimentEffect({
    isPreview,
    hasTierCards: cards.length > 0
  });

  /* Public counterpart of the in-app PPFE::HelpMeChoose gate (public pages have
     no portal gates, so this reads the public active-feature-flags list). Off
     by default — the quiz entry point stays hidden until the flag is enabled. */
  const {
    featureFlags
  } = usePublicFeatureFlags();
  const isHelpMeChooseEnabled = featureFlags.includes(FeatureFlags.PRICING_PAGE_HELP_ME_CHOOSE);
  const showCustomizePlanLink = featureFlags.includes(FeatureFlags.FSC_CONFIGURE_PAGE) && cards.length > 0;
  const handleCustomizePlanClick = useCallback(() => {
    trackClickedLink({
      type: 'internal',
      url: '/configure',
      name: 'fsc-customize-plan-link',
      trackingSource: TRACKING_SOURCES.FSC_CUSTOMIZE_PLAN_LINK,
      screen: Screens.FLEX_SEATS_AND_CREDITS
    });
  }, []);
  const [isQuizOpen, setIsQuizOpen] = useState(false);

  /* Owned here (on the always-mounted page) rather than inside HelpMeChooseInline
     so the impression stays once per page visit: the button unmounts/remounts on
     a currency change (tier query flips to loading → cards hidden), which would
     otherwise re-fire an instance-scoped guard. */
  const helpMeChooseImpressionRef = useRef(false);
  const handleTermChange = useCallback(term => {
    setSelectedTerm(term);
    trackTermSelectorInteraction({
      action: 'click',
      term,
      trackingSource: TRACKING_SOURCES.MERCH_CARD,
      screen: Screens.FLEX_SEATS_AND_CREDITS
    });
  }, [setSelectedTerm]);
  const hasTrackedTermViewRef = useRef(false);
  useEffect(() => {
    if (showCards && showTermToggle && !hasTrackedTermViewRef.current) {
      hasTrackedTermViewRef.current = true;
      trackTermSelectorInteraction({
        action: 'view',
        term: selectedTerm,
        trackingSource: TRACKING_SOURCES.MERCH_CARD,
        screen: Screens.FLEX_SEATS_AND_CREDITS
      });
    }
  }, [showCards, showTermToggle, selectedTerm]);

  /* Fire the merch-card-grid impression once, when the cards render. The grid
   * is the page's hero section (rendered on load, taller than the viewport), so
   * we fire on mount rather than via a scroll-into-view observer that a tall
   * container may not satisfy without scrolling. */
  const hasTrackedMerchCardsViewRef = useRef(false);
  useEffect(() => {
    if (showCards && !hasTrackedMerchCardsViewRef.current) {
      hasTrackedMerchCardsViewRef.current = true;
      trackSectionViewed({
        name: TRACKING_SOURCES.HSOC_MERCH_CARDS_SECTION,
        screen: Screens.FLEX_SEATS_AND_CREDITS,
        action: 'scrolled in view'
      });
    }
  }, [showCards]);
  const openQuiz = useCallback(() => setIsQuizOpen(true), []);
  const closeQuiz = useCallback(() => setIsQuizOpen(false), []);
  const planQuizData = useMemo(() => {
    var _tierOptions$products;
    return {
      productsByTier: (_tierOptions$products = tierOptions.productsByTier) !== null && _tierOptions$products !== void 0 ? _tierOptions$products : null,
      currencyCode: displayedCurrencyCode,
      selectedTerm,
      track: trackHsocPlanQuizEvent,
      designSystem: 'creative',
      renderPurchaseMotions: ({
        tierName,
        productApiName,
        motions,
        seatsQuantity
      }) => isHsocTierName(tierName) ? /*#__PURE__*/_jsx(HsocV2PublicPurchaseMotionContainer, {
        tierName: tierName,
        productApiName: productApiName,
        motions: motions,
        currencyCode: displayedCurrencyCode,
        term: selectedTerm,
        trackingSource: TRACKING_SOURCES.PLAN_QUIZ_MODAL,
        seatsQuantity: seatsQuantity
      }) : null
    };
  }, [tierOptions.productsByTier, displayedCurrencyCode, selectedTerm]);
  const helpMeChoose = isHelpMeChooseEnabled ? /*#__PURE__*/_jsx(HelpMeChooseInline, {
    onOpenPlanQuiz: openQuiz,
    impressionGuardRef: helpMeChooseImpressionRef
  }) : undefined;
  return /*#__PURE__*/_jsxs(PlanQuizDataProvider, {
    value: planQuizData,
    children: [/*#__PURE__*/_jsx(HsocV2PageHero, {
      currencyPicker: /*#__PURE__*/_jsx(PublicCurrencyPicker, {})
    }), tierOptions.status === 'loading' && /*#__PURE__*/_jsx(View, {
      padding: isMd ? '800' : '400',
      children: /*#__PURE__*/_jsx(HsocMerchCardGridSkeleton, {
        showHeader: true
      })
    }), tierOptions.status === 'error' && /*#__PURE__*/_jsx(View, {
      padding: isMd ? '800' : '400',
      children: /*#__PURE__*/_jsx(PublicMerchCardsError, {})
    }), showCards && /*#__PURE__*/_jsxs(View, {
      padding: isMd ? '800' : '400',
      children: [/*#__PURE__*/_jsx(NavMarker, {
        name: "HSOC_V2_MERCH_CARDS"
      }), /*#__PURE__*/_jsx("div", {
        id: "hsoc-tier-cards",
        children: /*#__PURE__*/_jsx(HsocMerchCardGrid, {
          cards: cards,
          showTermToggle: showTermToggle,
          activeTerm: selectedTerm,
          onTermChange: handleTermChange,
          helpMeChoose: helpMeChoose
        })
      })]
    }), /*#__PURE__*/_jsx(LoadablePublicHsocFeatureTable, {
      tierOptions: tierOptions,
      currencyCode: displayedCurrencyCode,
      selectedTerm: selectedTerm
    }), showCustomizePlanLink && /*#__PURE__*/_jsx(PublicFscCustomizePlanLink, {
      onClick: handleCustomizePlanClick
    }), /*#__PURE__*/_jsx(View, {
      as: "section",
      paddingInline: isMd ? '800' : '400',
      marginBlockStart: "1100",
      children: /*#__PURE__*/_jsx(HsocCreditsExplainerSection, {})
    }), /*#__PURE__*/_jsx(View, {
      as: "section",
      padding: isMd ? '800' : '400',
      marginBlockStart: "1400",
      marginBlockEnd: "1400",
      children: /*#__PURE__*/_jsx(HsocFaqsSection, {})
    }), isQuizOpen && /*#__PURE__*/_jsx(LoadablePlanQuizModal, {
      onClose: closeQuiz
    })]
  });
}
export default function PublicHsocPricingPage({
  isPreview = false
}) {
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      trackPageView({
        screen: Screens.FLEX_SEATS_AND_CREDITS
      });
    }
  }, []);
  return /*#__PURE__*/_jsxs(PublicPageWrapper, {
    errorBoundaryName: HUBSPOT_ON_CREDITS_PAGE,
    navMarkerName: "HSOC_V2_PAGE",
    metricsRoute: Screens.FLEX_SEATS_AND_CREDITS,
    chunkName: "public-hsoc-pricing-page",
    children: [/*#__PURE__*/_jsx(HsocBodyBackgroundStyles, {}), /*#__PURE__*/_jsx(PlanQuizProvider, {
      children: /*#__PURE__*/_jsx(HsocPublicPageContent, {
        isPreview: isPreview
      })
    })]
  });
}